import { useState, useEffect, useRef } from 'react'
import './App.css'
import { initializeChat } from './utils/gemini';

interface Story {
  id: number;
  username: string;
  imageUrl: string;
  viewed: boolean;
  mood?: string;
  moodEmoji?: string;
}

interface Comment {
  id: number;
  username: string;
  text: string;
  timestamp: string;
}

interface Post {
  id: number;
  username: string;
  userAvatar: string;
  imageUrl: string;
  caption: string;
  likes: number;
  comments: Comment[];
  timestamp: string;
  isVerified?: boolean;
  type: 'business' | 'personal';
  isLiked?: boolean;
}

interface ChatMessage {
  id: number;
  text: string;
  isUser: boolean;
  timestamp: string;
}

interface ChatSession {
  sendMessage(message: string): Promise<{
    response: {
      text(): string;
    };
  }>;
}

interface Mood {
  text: string;
  emoji: string;
  color: string;
}

function App() {
  const [stories, setStories] = useState<Story[]>([
    {
      id: 1,
      username: "your_updates",
      imageUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%230d6efd'/%3E%3Ctext x='50%25' y='50%25' font-size='20' fill='white' text-anchor='middle' dy='.3em'%3EYU%3C/text%3E%3C/svg%3E",
      viewed: false
    },
    {
      id: 2,
      username: "market_trends",
      imageUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23198754'/%3E%3Ctext x='50%25' y='50%25' font-size='20' fill='white' text-anchor='middle' dy='.3em'%3EMT%3C/text%3E%3C/svg%3E",
      viewed: false
    },
    {
      id: 3,
      username: "tech_insights",
      imageUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23dc3545'/%3E%3Ctext x='50%25' y='50%25' font-size='20' fill='white' text-anchor='middle' dy='.3em'%3ETI%3C/text%3E%3C/svg%3E",
      viewed: true
    },
    {
      id: 4,
      username: "global_news",
      imageUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%236f42c1'/%3E%3Ctext x='50%25' y='50%25' font-size='20' fill='white' text-anchor='middle' dy='.3em'%3EGN%3C/text%3E%3C/svg%3E",
      viewed: false
    }
  ]);

  const [posts, setPosts] = useState<Post[]>([
    {
      id: 1,
      username: "date_park",
      userAvatar: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%233b82f6'/%3E%3Ctext x='50%25' y='50%25' font-size='20' fill='white' text-anchor='middle' dy='.3em'%3EDP%3C/text%3E%3C/svg%3E",
      imageUrl: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1200' height='675' viewBox='0 0 1200 675'%3E%3Crect width='1200' height='675' fill='%23262626'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='48' fill='white' text-anchor='middle' dy='.3em'%3EWelcome to Chat%3C/text%3E%3Ctext x='50%25' y='60%25' font-family='Arial' font-size='24' fill='%233b82f6' text-anchor='middle' dy='.3em'%3EShare your moments with friends%3C/text%3E%3C/svg%3E",
      caption: "Welcome to Date Park! 👋 Share your moments, connect with friends, and explore amazing stories. Let's make every moment count! ✨",
      likes: 0,
      comments: [],
      timestamp: "Just now",
      isVerified: true,
      type: "business",
      isLiked: false
    }
  ]);

  const [newComment, setNewComment] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [newPostContent, setNewPostContent] = useState({
    caption: '',
    imageUrl: '',
    file: null as File | null
  });

  const [showMoodModal, setShowMoodModal] = useState(false);
  const [selectedMood, setSelectedMood] = useState<Mood | null>(null);

  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: 1,
      text: "Hi! I'm your friend bot. How are you feeling today?",
      isUser: false,
      timestamp: 'Just now'
    }
  ]);

  const [isTyping, setIsTyping] = useState(false);
  const [chat, setChat] = useState<ChatSession | null>(null);

  const [profilePic, setProfilePic] = useState("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%230d6efd'/%3E%3Ctext x='50%25' y='50%25' font-size='20' fill='white' text-anchor='middle' dy='.3em'%3ECU%3C/text%3E%3C/svg%3E");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const moods: Mood[] = [
    { text: 'Happy', emoji: '😊', color: '#FFD700' },
    { text: 'Sad', emoji: '😢', color: '#4169E1' },
    { text: 'Angry', emoji: '😠', color: '#FF4500' },
    { text: 'Excited', emoji: '🤩', color: '#FF69B4' },
    { text: 'Peaceful', emoji: '😌', color: '#98FB98' },
    { text: 'Grateful', emoji: '🙏', color: '#DDA0DD' }
  ];

  const [lastMoodTime, setLastMoodTime] = useState<number>(0);
  const MOOD_COOLDOWN = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  useEffect(() => {
    setChat(initializeChat());
  }, []);

  const handleLike = (postId: number) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          likes: post.isLiked ? post.likes - 1 : post.likes + 1,
          isLiked: !post.isLiked
        };
      }
      return post;
    }));
  };

  const handleComment = (postId: number) => {
    if (!newComment.trim()) return;

    setPosts(posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          comments: [
            ...post.comments,
            {
              id: Date.now(),
              username: 'current_user',
              text: newComment,
              timestamp: 'Just now'
            }
          ]
        };
      }
      return post;
    }));
    setNewComment('');
  };

  const handleShare = (post: Post) => {
    setSelectedPost(post);
    setShowShareModal(true);
  };

  const handleNewPost = () => {
    setShowNewPostModal(true);
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPostContent(prev => ({
          ...prev,
          imageUrl: reader.result as string,
          file: file
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const createNewPost = () => {
    if (!newPostContent.caption.trim() || !newPostContent.imageUrl) return;

    const newPost: Post = {
      id: Date.now(),
      username: 'current_user',
      userAvatar: profilePic,
      imageUrl: newPostContent.imageUrl,
      caption: newPostContent.caption,
      likes: 0,
      comments: [],
      timestamp: 'Just now',
      type: 'personal',
      isLiked: false
    };

    setPosts([newPost, ...posts]);
    setNewPostContent({ caption: '', imageUrl: '', file: null });
    setShowNewPostModal(false);
  };

  const handleStoryClick = (storyId: number) => {
    setStories(stories.map(story => 
      story.id === storyId ? { ...story, viewed: true } : story
    ));
  };

  const handleMoodSelect = (mood: Mood) => {
    setSelectedMood(mood);
  };

  const handleAddMood = () => {
    if (!selectedMood) return;

    const currentTime = Date.now();
    if (currentTime - lastMoodTime < MOOD_COOLDOWN) {
      const remainingTime = Math.ceil((MOOD_COOLDOWN - (currentTime - lastMoodTime)) / (1000 * 60 * 60));
      alert(`You can only add a new mood story once every 24 hours. Please wait ${remainingTime} hours.`);
      return;
    }

    // Check if user already has a mood story
    const existingMoodStory = stories.find(story => 
      story.username === 'current_user' && story.mood
    );

    if (existingMoodStory) {
      // Update existing mood story
      setStories(stories.map(story => 
        story.id === existingMoodStory.id
          ? {
              ...story,
              mood: selectedMood.text,
              moodEmoji: selectedMood.emoji,
              imageUrl: `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23${selectedMood.color.slice(1)}'/%3E%3Ctext x='50%25' y='50%25' font-size='20' fill='white' text-anchor='middle' dy='.3em'%3E${selectedMood.emoji}%3C/text%3E%3C/svg%3E`,
              viewed: false
            }
          : story
      ));
    } else {
      // Add new mood story
      const newStory: Story = {
        id: Date.now(),
        username: 'current_user',
        imageUrl: `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%23${selectedMood.color.slice(1)}'/%3E%3Ctext x='50%25' y='50%25' font-size='20' fill='white' text-anchor='middle' dy='.3em'%3E${selectedMood.emoji}%3C/text%3E%3C/svg%3E`,
        viewed: false,
        mood: selectedMood.text,
        moodEmoji: selectedMood.emoji
      };

      setStories([newStory, ...stories]);
    }

    setLastMoodTime(currentTime);
    setSelectedMood(null);
    setShowMoodModal(false);
  };

  const handleSendMessage = async () => {
    if (!chatMessage.trim() || isTyping || !chat) return;

    const userMessage: ChatMessage = {
      id: Date.now(),
      text: chatMessage,
      isUser: true,
      timestamp: 'Just now'
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatMessage('');
    setIsTyping(true);

    try {
      const result = await chat.sendMessage(chatMessage);
      const response = await result.response;
      const botMessage: ChatMessage = {
        id: Date.now() + 1,
        text: response.text(),
        isUser: false,
        timestamp: 'Just now'
      };

      setChatMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error getting response from Gemini:', error);
      const errorMessage: ChatMessage = {
        id: Date.now() + 1,
        text: "I'm having trouble responding right now. Could you try again?",
        isUser: false,
        timestamp: 'Just now'
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleProfilePicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePic(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="instagram-clone">
      <header className="app-header">
        <div className="header-content">
          <h1 className="app-title">DATE PARK</h1>
          <div className="header-actions">
            <button className="new-post-btn" onClick={handleNewPost}>
              <svg aria-label="New post" color="rgb(0, 0, 0)" fill="rgb(0, 0, 0)" height="24" role="img" viewBox="0 0 24 24" width="24">
                <path d="M2 12v3.45c0 2.849.698 4.005 1.606 4.944.94.909 2.098 1.608 4.946 1.608h6.896c2.848 0 4.006-.7 4.946-1.608C21.302 19.455 22 18.3 22 15.45V8.552c0-2.849-.698-4.006-1.606-4.945C19.454 2.7 18.296 2 15.448 2H8.552c-2.848 0-4.006.699-4.946 1.607C2.698 4.547 2 5.703 2 8.552Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                <line fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" x1="6.545" x2="17.455" y1="12.001" y2="12.001"></line>
                <line fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" x1="12.003" x2="12.003" y1="6.545" y2="17.455"></line>
              </svg>
            </button>
            <div className="profile-pic-container" onClick={() => fileInputRef.current?.click()}>
              <img 
                src={profilePic} 
                alt="Profile" 
                className="profile-pic" 
              />
              <div className="profile-pic-edit-overlay">
                <svg 
                  className="profile-pic-edit-icon"
                  aria-label="Edit profile picture" 
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.05c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path>
                </svg>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleProfilePicChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
            </div>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="stories-container">
          <div className="stories-wrapper">
            <div 
              className="story-item add-story"
              onClick={() => setShowMoodModal(true)}
            >
              <div className="story-avatar">
                <svg aria-label="Add story" color="rgb(0, 0, 0)" fill="rgb(0, 0, 0)" height="24" role="img" viewBox="0 0 24 24" width="24">
                  <path d="M2 12v3.45c0 2.849.698 4.005 1.606 4.944.94.909 2.098 1.608 4.946 1.608h6.896c2.848 0 4.006-.7 4.946-1.608C21.302 19.455 22 18.3 22 15.45V8.552c0-2.849-.698-4.006-1.606-4.945C19.454 2.7 18.296 2 15.448 2H8.552c-2.848 0-4.006.699-4.946 1.607C2.698 4.547 2 5.703 2 8.552Z" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                  <line fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" x1="6.545" x2="17.455" y1="12.001" y2="12.001"/>
                  <line fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" x1="12.003" x2="12.003" y1="6.545" y2="17.455"/>
                </svg>
              </div>
              <span className="story-username">Add Mood</span>
            </div>
            {stories.map(story => (
              <div 
                key={story.id} 
                className={`story-item ${story.viewed ? 'viewed' : ''}`}
                onClick={() => handleStoryClick(story.id)}
              >
                <div className="story-avatar">
                  <img src={story.imageUrl} alt={story.username} />
                </div>
                <span className="story-username">{story.username}</span>
                {story.mood && (
                  <span className="story-mood">
                    {story.moodEmoji} {story.mood}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="posts-container">
          {posts.map(post => (
            <article key={post.id} className="post-card">
              <div className="post-header">
                <img src={post.userAvatar} alt={post.username} className="post-avatar" />
                <div className="post-header-info">
                  <div className="post-username">
                    {post.username}
                    {post.isVerified && (
                      <svg aria-label="Verified" className="verified-badge" color="rgb(0, 149, 246)" fill="rgb(0, 149, 246)" height="12" role="img" viewBox="0 0 40 40" width="12">
                        <path d="M19.998 3.094 14.638 0l-2.972 5.15H5.432v6.354L0 14.64 3.094 20 0 25.359l5.432 3.137v5.905h5.975L14.638 40l5.36-3.094L25.358 40l3.232-5.6h6.162v-6.01L40 25.359 36.905 20 40 14.641l-5.248-3.03v-6.46h-6.419L25.358 0l-5.36 3.094Zm7.415 11.225 2.254 2.287-11.43 11.5-6.835-6.93 2.244-2.258 4.587 4.581 9.18-9.18Z" fillRule="evenodd"></path>
                      </svg>
                    )}
                  </div>
                </div>
              </div>
              <div className="post-image">
                <img src={post.imageUrl} alt="Post content" />
              </div>
              <div className="post-actions">
                <div className="action-buttons">
                  <button 
                    className={`action-btn ${post.isLiked ? 'liked' : ''}`}
                    onClick={() => handleLike(post.id)}
                  >
                    {post.isLiked ? (
                      <svg aria-label="Unlike" color="rgb(255, 48, 64)" fill="rgb(255, 48, 64)" height="24" role="img" viewBox="0 0 48 48" width="24">
                        <path d="M34.6 3.1c-4.5 0-7.9 1.8-10.6 5.6-2.7-3.7-6.1-5.5-10.6-5.5C6 3.1 0 9.6 0 17.6c0 7.3 5.4 12 10.6 16.5.6.5 1.3 1.1 1.9 1.7l2.3 2c4.4 3.9 6.6 5.9 7.6 6.5.5.3 1.1.5 1.6.5s1.1-.2 1.6-.5c1-.6 2.8-2.2 7.8-6.8l2-1.8c.7-.6 1.3-1.2 2-1.7C42.7 29.6 48 25 48 17.6c0-8-6-14.5-13.4-14.5z"></path>
                      </svg>
                    ) : (
                      <svg aria-label="Like" color="rgb(38, 38, 38)" fill="rgb(38, 38, 38)" height="24" role="img" viewBox="0 0 24 24" width="24">
                        <path d="M16.792 3.904A4.989 4.989 0 0 1 21.5 9.122c0 3.072-2.652 4.959-5.197 7.222-2.512 2.243-3.865 3.469-4.303 3.752-.477-.309-2.143-1.823-4.303-3.752C5.141 14.072 2.5 12.167 2.5 9.122a4.989 4.989 0 0 1 4.708-5.218 4.21 4.21 0 0 1 3.675 1.941c.84 1.175.98 1.763 1.12 1.763s.278-.588 1.11-1.766a4.17 4.17 0 0 1 3.679-1.938m0-2a6.04 6.04 0 0 0-4.797 2.127 6.052 6.052 0 0 0-4.787-2.127A6.985 6.985 0 0 0 .5 9.122c0 3.61 2.55 5.827 5.015 7.97.283.246.569.494.853.747l1.027.918a44.998 44.998 0 0 0 3.518 3.018 2 2 0 0 0 2.174 0 45.263 45.263 0 0 0 3.626-3.115l.922-.824c.293-.26.59-.519.885-.774 2.334-2.025 4.98-4.32 4.98-7.94a6.985 6.985 0 0 0-6.708-7.218Z"></path>
                      </svg>
                    )}
                  </button>
                  <button className="action-btn" onClick={() => (document.querySelector(`#comment-input-${post.id}`) as HTMLInputElement)?.focus()}>
                    <svg aria-label="Comment" color="rgb(0, 0, 0)" fill="rgb(0, 0, 0)" height="24" role="img" viewBox="0 0 24 24" width="24">
                      <path d="M20.656 17.008a9.993 9.993 0 1 0-3.59 3.615L22 22Z" fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="2"></path>
                    </svg>
                  </button>
                  <button className="action-btn" onClick={() => handleShare(post)}>
                    <svg aria-label="Share Post" color="rgb(0, 0, 0)" fill="rgb(0, 0, 0)" height="24" role="img" viewBox="0 0 24 24" width="24">
                      <line fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="2" x1="22" x2="9.218" y1="3" y2="10.083"></line>
                      <polygon fill="none" points="11.698 20.334 22 3.001 2 3.001 9.218 10.084 11.698 20.334" stroke="currentColor" strokeLinejoin="round" strokeWidth="2"></polygon>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="post-info">
                <p className="likes">{post.likes.toLocaleString()} likes</p>
                <p className="caption">
                  <strong>{post.username}</strong> {post.caption}
                </p>
                {post.comments.length > 0 && (
                  <div className="comments-section">
                    {post.comments.map(comment => (
                      <div key={comment.id} className="comment">
                        <strong>{comment.username}</strong> {comment.text}
                        <span className="comment-timestamp">{comment.timestamp}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="timestamp">{post.timestamp}</p>
                <div className="comment-form">
                  <input
                    id={`comment-input-${post.id}`}
                    type="text"
                    className="comment-input"
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleComment(post.id)}
                  />
                  <button 
                    className="comment-submit"
                    onClick={() => handleComment(post.id)}
                    disabled={!newComment.trim()}
                  >
                    Post
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </main>

      <div className={`chat-widget ${isChatOpen ? 'open' : ''}`}>
        <div className="chat-header" onClick={() => setIsChatOpen(!isChatOpen)}>
          <span className="chat-title">Chat with AI Friend</span>
          <button className="chat-toggle">
            {isChatOpen ? '▼' : '▲'}
          </button>
        </div>
        {isChatOpen && (
          <>
            <div className="chat-messages">
              {chatMessages.map(message => (
                <div 
                  key={message.id} 
                  className={`chat-message ${message.isUser ? 'user' : 'bot'}`}
                >
                  <div className="message-content">
                    <p>{message.text}</p>
                    <span className="message-time">{message.timestamp}</span>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="chat-message bot">
                  <div className="message-content typing">
                    <span className="typing-indicator">●</span>
                    <span className="typing-indicator">●</span>
                    <span className="typing-indicator">●</span>
                  </div>
                </div>
              )}
            </div>
            <div className="chat-input">
              <input
                type="text"
                placeholder="Type a message..."
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                disabled={isTyping}
              />
              <button 
                onClick={handleSendMessage}
                disabled={!chatMessage.trim() || isTyping}
              >
                Send
              </button>
            </div>
          </>
        )}
      </div>

      {showShareModal && selectedPost && (
        <>
          <div className="modal-overlay" onClick={() => setShowShareModal(false)} />
          <div className="share-modal">
            <div className="share-modal-content">
              <h3>Share Post</h3>
              <div className="share-options">
                <button onClick={() => {
                  navigator.clipboard.writeText(`Check out this post by ${selectedPost.username}: ${selectedPost.caption}`);
                  setShowShareModal(false);
                }}>
                  Copy Link
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {showNewPostModal && (
        <>
          <div className="modal-overlay" onClick={() => setShowNewPostModal(false)} />
          <div className="new-post-modal">
            <div className="new-post-content">
              <h3>Create New Post</h3>
              <div className="image-upload-container">
                {newPostContent.imageUrl ? (
                  <div className="image-preview">
                    <img src={newPostContent.imageUrl} alt="Preview" />
                    <button 
                      className="change-image-btn"
                      onClick={() => setNewPostContent(prev => ({ ...prev, imageUrl: '', file: null }))}
                    >
                      Change Image
                    </button>
                  </div>
                ) : (
                  <div className="upload-prompt">
                    <svg aria-label="Upload image" color="rgb(255, 255, 255)" fill="rgb(255, 255, 255)" height="32" role="img" viewBox="0 0 24 24" width="32">
                      <path d="M21.5 0h-19A2.5 2.5 0 000 2.5v19A2.5 2.5 0 002.5 24h19a2.5 2.5 0 002.5-2.5v-19A2.5 2.5 0 0021.5 0zM20 21.5a.5.5 0 01-.5.5h-15a.5.5 0 01-.5-.5v-15a.5.5 0 01.5-.5h15a.5.5 0 01.5.5v15zM12 6.5A5.5 5.5 0 116.5 12 5.5 5.5 0 0112 6.5zm0 8.25A2.75 2.75 0 109.25 12 2.75 2.75 0 0012 14.75z"/>
                    </svg>
                    <p>Click to upload an image</p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      style={{ display: 'none' }}
                      id="post-image-input"
                    />
                    <label htmlFor="post-image-input" className="upload-btn">
                      Select from device
                    </label>
                  </div>
                )}
              </div>
              <textarea
                placeholder="Write a caption..."
                value={newPostContent.caption}
                onChange={(e) => setNewPostContent(prev => ({ ...prev, caption: e.target.value }))}
              />
              <button 
                onClick={createNewPost} 
                disabled={!newPostContent.caption.trim() || !newPostContent.imageUrl}
                className="share-post-btn"
              >
                Share
              </button>
            </div>
          </div>
        </>
      )}

      {showMoodModal && (
        <>
          <div className="modal-overlay" onClick={() => {
            setShowMoodModal(false);
            setSelectedMood(null);
          }} />
          <div className="mood-modal">
            <div className="mood-modal-content">
              <h3>How are you feeling today?</h3>
              <div className="mood-options">
                {moods.map((mood) => (
                  <button 
                    key={mood.text}
                    className={`mood-option-btn ${selectedMood?.text === mood.text ? 'selected' : ''}`}
                    onClick={() => handleMoodSelect(mood)}
                  >
                    <span className="mood-emoji">{mood.emoji}</span>
                    <span className="mood-text">{mood.text}</span>
                  </button>
                ))}
              </div>
              {selectedMood && (
                <div className="mood-confirmation">
                  <p>Selected Mood: {selectedMood.emoji} {selectedMood.text}</p>
                  <button 
                    className="post-mood-btn"
                    onClick={handleAddMood}
                  >
                    Post Story
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default App
